import React, { FC, Suspense, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import CategoryPageConstructor from 'src/constructors/pageConstructors/CategoryPageConstructor';
import ContentPageConstructor from 'src/constructors/pageConstructors/ContentPageConstructor';
import HomePageConstructor from 'src/constructors/pageConstructors/HomePageConstructor';
import StaticPageConstructor from 'src/constructors/pageConstructors/StaticPagesConstructor';
import { envStrToBoolean, getCategorySlug, isTokenExpired } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useRefreshToken } from 'src/hooks/use-refresh-token';
import { InfoAndRulesPage } from 'src/pages/InfoAndRulesPage/InfoAndRulesPage';
import { Signup } from 'src/pages/Signup/Signup';
import { setSelectedCategory } from 'src/store/app/actions';
import { setSelectedProvider, setSelectedTag } from 'src/store/games/actions';
import { setLogout } from 'src/store/user/actions';
import { NavigationItem } from 'src/types/res-dto/categories-res';
import { RootState } from 'src/types/store-types';
import { PAPIGAMES_AFFILATES } from 'src/utils/constants';
import { lazyWithCustomError } from 'src/utils/lazyWithCustomError';
import Iframe from '../base-components/Iframe';
import MainLayout from '../layouts/MainLayout';

const NotFoundPage = lazyWithCustomError(() => import('src/pages/NotFoundPage'));
const BetConstructSportsbook = lazyWithCustomError(() => import('../base-components/BetConstruct_Sportsbook'));
const CasinoTournament = lazyWithCustomError(() => import('../base-components/CasinoTournament'));
const Cms_Wager = lazyWithCustomError(() => import('../base-components/CMS_Wager'));
const CopaPapi = lazyWithCustomError(() => import('../base-components/CopaPapi'));
const FunMission = lazyWithCustomError(() => import('../base-components/FunMission'));
const Loyalty = lazyWithCustomError(() => import('../base-components/Loyalty'));
const Race = lazyWithCustomError(() => import('../base-components/Race'));
const RaffleDraw = lazyWithCustomError(() => import('../base-components/RaffleDraw'));
const SafePromo = lazyWithCustomError(() => import('../base-components/SafePromo'));

// Nav types whose route is a top-level `/{slug}` rather than `/category/{slug}`.
const TOP_LEVEL_TYPES = ['infoAndRulesPage', 'static'];

// Resolve a stale pre-migration URL to its canonical one; only an exact name match redirects, so 404s fall through.
const canonicalPathFor = (navigation: NavigationItem[], pathname: string): string | null => {
  const categoryMatch = pathname.match(/^\/category\/([^/]+)\/?$/);
  if (categoryMatch) {
    const segment = decodeURIComponent(categoryMatch[1]);
    const slug = getCategorySlug(navigation, segment);
    return slug === segment ? null : `/category/${slug}`;
  }

  // `/termsConditions` -> `/terms-conditions`: these used to work via slugAliases, which were removed.
  const topLevelMatch = pathname.match(/^\/([^/]+)\/?$/);
  if (topLevelMatch) {
    const segment = decodeURIComponent(topLevelMatch[1]);
    const item = navigation.find((n) => n?.name === segment && TOP_LEVEL_TYPES.includes(n?.type));
    const shouldRedirect = Boolean(item?.slug) && item?.slug !== segment;
    return shouldRedirect ? `/${item?.slug}` : null;
  }

  return null;
};

// Send stale pre-migration URLs to the canonical slug URL; real 404s fall through to NotFoundPage.
const NotFoundOrCategoryRedirect: FC = () => {
  const { navigation } = useSelector((state: RootState) => state.configs);
  const { pathname } = useLocation();

  if (navigation?.length) {
    const canonical = canonicalPathFor(navigation, pathname);
    if (canonical) {
      return <Navigate to={canonical} replace />;
    }
  }

  return (
    <Suspense fallback={null}>
      <NotFoundPage />
    </Suspense>
  );
};

type Route = {
  paths: string[];
  element: JSX.Element;
  isDefault?: boolean;
};
const Routing: FC = () => {
  const _logout = useConnectedAction(setLogout);
  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const _setSelectedProvider = useConnectedAction(setSelectedProvider);
  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { pageConfigs, navigation } = useSelector((state: RootState) => state.configs);

  useRefreshToken();

  const renderPage = (route: NavigationItem): any => {
    let component;
    let sections;
    let mainPath: string;
    const childrenPaths: string[] = [];

    switch (route?.type) {
      case 'home':
        sections = pageConfigs.filter((f: { type: string }) => f.type === 'home')[0]?.sections;
        component = <HomePageConstructor settings={sections} type={route.type} />;
        mainPath = '/home';
        break;
      case 'signup':
        mainPath = '/signup';
        component = <Signup />;
        break;
      case 'infoAndRulesPage':
        mainPath = `/${route.slug}`;
        component = <InfoAndRulesPage slug={route.slug} name={route.name} />;
        break;
      case 'static':
        childrenPaths.push(':staticPathParam');
        childrenPaths.push(':tab');
        component = <StaticPageConstructor page={route.name} />;
        mainPath = route.slug;
        break;
      case 'fun-mission':
        component = <FunMission />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'raffleDraw':
        component = <RaffleDraw />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'copaPapi':
        component = <CopaPapi />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'casinoTournament':
        component = <CasinoTournament />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'race':
      case 'fsRace':
        component = <Race />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'safe-promo':
        component = <SafePromo />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'loyalty':
        component = <Loyalty />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'staticIframe':
      case 'tombala':
      case 'pokerIframe':
      case 'cybersport':
        component = <Iframe type={route?.type} />;
        mainPath = `/category/${route.slug}`;
        break;
      case 'liveSport':
      case 'sport':
        const sportType = route.type === 'liveSport' ? 'live' : 'sport';

        component = <Iframe type={route?.type} />;

        if (envStrToBoolean(process.env.REACT_APP_CMS_WAGER)) {
          component = <Cms_Wager sportType={sportType} />;
        }

        if (envStrToBoolean(process.env.REACT_APP_BC_SPORT_BOOK)) {
          component = <BetConstructSportsbook sportType={sportType} />;
        }

        mainPath = `/category/${route.slug}`;
        break;
      case 'category':
        if (route?.subType === 'staticPage') {
          component = <StaticPageConstructor page={route.name} />;
        } else {
          sections = pageConfigs.filter((f: { type: string }) => f.type === route.type)[0]?.sections;
          component = (
            <CategoryPageConstructor
              type={route.type}
              categorySlug={route.slug}
              categoryName={route.name}
              settings={sections}
            />
          );
        }

        mainPath = `/category/${route.slug}`;
        break;
      case 'content':
        const contentPages = pageConfigs.filter((f: { type: string }) => f.type === route.type);
        sections = contentPages.find((c: { sections: { [x: string]: any } }) => c.sections[route.name])?.sections;
        component = <ContentPageConstructor settings={sections} />;
        mainPath = `/content/${route.slug}`;
        break;
      default:
        component = <></>;
        mainPath = '';
    }

    const paths = [mainPath];

    if (childrenPaths.length) {
      childrenPaths.reduce((prev, childrenPath) => {
        const path = `${prev}/${childrenPath}`;
        paths.push(path);

        return path;
      }, mainPath);
    }

    return {
      element: component,
      paths,
      isDefault: route.isDefault,
    };
  };

  const routes: Route[] = useMemo(() => {
    const routesArr: Route[] = [];
    if (navigation && !!pageConfigs) {
      navigation?.forEach((route: NavigationItem) => {
        routesArr.push(renderPage(route));
      });
    }
    return routesArr;
  }, [navigation, pageConfigs]);

  const defaultRoute = useMemo(() => navigation?.find((route: NavigationItem) => route.isDefault), [navigation]);

  const defaultPage = useMemo(
    () => routes.find((route) => route.isDefault)?.element ?? routes[0]?.element ?? <></>,
    [routes]
  );

  useEffect(() => {
    if (selectedCategory !== 'home') return;
    if (!defaultRoute) return;
    if (defaultRoute.type !== 'category' || !defaultRoute.name) return;

    _setSelectedCategory(defaultRoute.name);
    _setSelectedTag(defaultRoute.hasHomeTag ? 'home' : 'allGames');
    _setSelectedProvider(['all_providers']);
  }, [defaultRoute, selectedCategory, _setSelectedCategory, _setSelectedTag, _setSelectedProvider]);

  useEffect(() => {
    if (isTokenExpired()) {
      _logout();
    }
  }, []);

  return (
    <Suspense fallback={<div />}>
      <Routes>
        {process.env.REACT_APP_WEBSITENAME === 'PapiGames' && (
          <>
            {PAPIGAMES_AFFILATES.map((affilate) => (
              <Route path={`/${affilate.name}`} key={affilate.id} element={<MainLayout children={defaultPage} />} />
            ))}
          </>
        )}
        <Route path={'/'} element={<MainLayout children={defaultPage} />} />

        {routes && !!routes.length && <Route path={'/*'} element={<NotFoundOrCategoryRedirect />} />}

        {routes?.map((route) => {
          return route.paths.map((path) => (
            <Route key={path} path={path} element={<MainLayout children={route.element} />} />
          ));
        })}
      </Routes>
    </Suspense>
  );
};
export default Routing;
